/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { getFetchData, isRequestFailure, useFetch } from '../../../hooks/useFetch';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import { FormActions, FormRow, FormSection, FormShell } from '../../ui';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import { getFormattedLabel } from '../../../utils/FormatFunctions';

interface ModuleWeekFormProps {
  defaultValues?: any;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function ModuleWeekForm({ defaultValues = {} }: ModuleWeekFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/weeks';
  const { requestApi } = useFetch(baseUrl);
  const {
    requestApi: reqApiCategories,
    data: dataCategories,
    loading: loadingCategories,
  } = useFetch('/v1/admin/categories');
  const { data: optionsCategories, pagination: paginationCategories } =
    getFetchData(dataCategories);
  const { itemId, moduleId } = useParams();
  const navigate = useNavigate();
  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome da semana',
      label: 'Nome',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'category_id',
      value: defaultValues?.category ? getFormattedLabel(defaultValues?.category) : null,
      placeholder: 'Selecione uma categoria',
      label: 'Categoria',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const category = formInputs.find((field) => field.name === 'category_id');
  const status = formInputs.find((field) => field.name === 'status');

  const onSubmit = async (values: any) => {
    const params = {
      ...values,
      category_id: values.category_id.value,
      school_class_id: moduleId,
    };
    const result = await requestApi(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    if (!isRequestFailure(result)) navigate(`/contents/modules/${moduleId}/weeks`);
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  const isEdit = Boolean(defaultValues.name);

  return (
    <FormShell
      title={isEdit ? 'Editar Semana' : 'Criar Semana'}
      backTo={`/contents/modules/${moduleId}/weeks`}
      backLabel="Semanas"
    >
      <Box
        as="form"
        noValidate
        onSubmit={handleSubmit(onSubmit, validationToast)}
        display="flex"
        flexDirection="column"
        gap="16px"
      >
        <FormSection title="Informações Básicas">
          <FormRow>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
            <SelectInputForm
              name={category?.name}
              label={category?.label}
              options={[
                ...(optionsCategories
                  ?.filter((item) => item.status !== false)
                  .map((item) => getFormattedLabel(item)) ?? []),
              ]}
              isLoading={loadingCategories}
              pagination={paginationCategories}
              getOptions={(filters) => reqApiCategories('get', { params: { ...filters } })}
              placeholder={category?.placeholder}
              isRequired
              control={control}
              errors={errors}
            />
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              errors={errors}
              control={control}
            />
          </FormRow>
        </FormSection>

        <FormActions cancelTo={`/contents/modules/${moduleId}/weeks`} isLoading={isSubmitting} />
      </Box>
    </FormShell>
  );
}
